import { useEffect, useState } from "react";
import { Area, AreaChart, ResponsiveContainer } from "recharts";
import { Trophy, Zap } from "lucide-react";
import { api } from "@/lib/api";
import { PERFORMANCE } from "@/lib/content";

const SAMPLE_CHART = [8, 12, 10, 16, 14, 20, 19, 26, 24, 32, 30, 40].map((v, i) => ({ i, v }));

export const fmtUnits = (u) => `${u > 0 ? "+" : ""}${(u || 0).toFixed(2)}u`;
export const unitColor = (u) => (u > 0 ? "text-emerald-400" : u < 0 ? "text-red-400" : "text-slate-300");
export const sinceLabel = (iso) => (iso ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "today");

export function usePerformance() {
  const [perf, setPerf] = useState(null);
  useEffect(() => {
    api.get("/performance").then((r) => setPerf(r.data)).catch(() => setPerf(null));
  }, []);
  return perf;
}

export function PerformanceTracker({ perf }) {
  const real = perf?.ready;
  const chart = real && perf.chart.length > 1 ? perf.chart : SAMPLE_CHART;
  const streak = real ? perf.win_streak : PERFORMANCE.winStreak;
  const tile = (f, sample) => (real ? { u: perf[f].units, txt: fmtUnits(perf[f].units), rec: perf[f].record } : { u: 1, txt: sample, rec: null });
  return (
    <div className="panel p-4" data-testid="performance-tracker">
      <div className="flex items-center gap-2 font-heading font-bold tracking-wide text-smasher-gold mb-3"><Trophy className="w-4 h-4" /> PERFORMANCE TRACKER</div>
      <div className="text-xs text-slate-400 font-bold">WIN STREAK</div>
      <div className="flex items-end justify-between">
        <div className="font-heading font-black text-5xl text-white leading-none" data-testid="win-streak">{streak}<span className="text-sm text-slate-400 ml-1">DAYS</span></div>
        <div className="w-28 h-14">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chart}>
              <defs>
                <linearGradient id="g" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10B981" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <Area type="monotone" dataKey="v" stroke="#10B981" strokeWidth={2} fill="url(#g)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 mt-3 border-t border-white/5 pt-3">
        {[["THIS WEEK", "this_week", PERFORMANCE.thisWeek], ["THIS MONTH", "this_month", PERFORMANCE.thisMonth], ["ALL TIME", "all_time", PERFORMANCE.allTime]].map(([k, f, sample]) => {
          const t = tile(f, sample);
          return (
            <div key={k} className="text-center" data-testid={`perf-${f}`}>
              <div className="text-[8px] text-slate-500 tracking-wide">{k}</div>
              <div className={`font-mono-num font-bold text-sm ${unitColor(t.u)}`}>{t.txt}</div>
              {t.rec && <div className="text-[9px] text-slate-500 font-mono-num">{t.rec}</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function Last7Days({ perf }) {
  const real = perf?.ready;
  const s = real ? perf.last7 : { record: PERFORMANCE.last7Record, win_rate: PERFORMANCE.last7Rate };
  return (
    <div className="panel p-4 flex items-center justify-between" data-testid="last-7-days">
      <div>
        <div className="flex items-center gap-2 font-heading font-bold tracking-wide text-smasher-gold mb-1 text-sm"><Zap className="w-4 h-4" /> LAST 7 DAYS</div>
        <div className="font-heading font-black text-3xl text-white" data-testid="last7-record">{s.record}</div>
        <div className="text-emerald-400 font-bold text-sm" data-testid="last7-rate">{s.win_rate}% WIN RATE</div>
        {real && <div className={`text-xs font-mono-num ${unitColor(s.units)}`} data-testid="last7-units">{fmtUnits(s.units)} · free picks</div>}
      </div>
      <div className="relative w-20 h-20">
        <svg viewBox="0 0 36 36" className="w-20 h-20 -rotate-90">
          <circle cx="18" cy="18" r="15.9" fill="none" stroke="#1f2430" strokeWidth="3.5" />
          <circle cx="18" cy="18" r="15.9" fill="none" stroke="#10B981" strokeWidth="3.5" strokeDasharray={`${s.win_rate} 100`} strokeLinecap="round" />
        </svg>
        <span className="absolute inset-0 flex items-center justify-center font-mono-num font-bold text-white">{s.win_rate}%</span>
      </div>
    </div>
  );
}
